import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { draftManager } from '../lib/draftManager.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { formatDateTime } from '../lib/dateUtils';
import type { DraftEntityType, UserDraft, DraftSummary } from '@gng-ehs/shared';

const TYPE_CONFIG: Record<
  DraftEntityType,
  { label: string; icon: string; path: string; bg: string; border: string; text: string }
> = {
  training: { label: 'Training', icon: '🎓', path: '/training', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
  form9: { label: 'Form 9', icon: '📋', path: '/form9', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  form10: { label: 'Form 10', icon: '📋', path: '/form10', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  form11: { label: 'Form 11', icon: '🧪', path: '/form11', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' },
  equipment: { label: 'Equipment', icon: '⚙️', path: '/equipment', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
  company: { label: 'Company', icon: '🏭', path: '/companies', bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700' },
  personnel: { label: 'Personnel', icon: '👤', path: '/personnel', bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700' },
};

function formatRelativeTime(dateStr: string): string {
  try {
    const time = new Date(dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`).getTime();
    const diffSec = Math.floor((Date.now() - time) / 1000);
    if (diffSec < 60) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDateTime(dateStr);
  } catch {
    return 'Recently';
  }
}

export function DraftsPage() {
  const navigate = useNavigate();

  const [drafts, setDrafts] = useState<UserDraft[]>(() => draftManager.listDrafts());
  const [selectedType, setSelectedType] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Confirm dialogs
  const [deleteItem, setDeleteItem] = useState<UserDraft | null>(null);
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [notification, setNotification] = useState('');

  // Reload drafts from store
  const refreshDrafts = useCallback(() => {
    setDrafts(draftManager.listDrafts());
  }, []);

  // Sync on mount and subscribe to changes
  useEffect(() => {
    refreshDrafts();
    draftManager.syncFromServer().then(() => refreshDrafts());
    const unsub = draftManager.subscribe(() => refreshDrafts());
    return unsub;
  }, [refreshDrafts]);

  // Dismiss notification
  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => setNotification(''), 3500);
    return () => clearTimeout(timer);
  }, [notification]);

  // Counts summary per entity type
  const summary: DraftSummary = useMemo(() => {
    const counts: DraftSummary = {
      all: drafts.length,
      training: 0,
      form9: 0,
      form10: 0,
      form11: 0,
      equipment: 0,
      company: 0,
      personnel: 0,
    };
    for (const d of drafts) {
      if (d.entity_type in counts) {
        counts[d.entity_type]++;
      }
    }
    return counts;
  }, [drafts]);

  // Filtered drafts based on active pill and search text
  const filteredDrafts = useMemo(() => {
    return drafts.filter((d) => {
      const matchType = selectedType === 'all' || d.entity_type === selectedType;
      if (!matchType) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const titleMatch = (d.title || '').toLowerCase().includes(q);
      const subMatch = (d.subtitle || '').toLowerCase().includes(q);
      const dataMatch = JSON.stringify(d.form_data).toLowerCase().includes(q);
      return titleMatch || subMatch || dataMatch;
    });
  }, [drafts, selectedType, search]);

  const toggleDetails = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleResume = (draft: UserDraft) => {
    const config = TYPE_CONFIG[draft.entity_type];
    if (!config) return;
    navigate(`${config.path}?resumeDraft=${draft.id}`);
  };

  const handleDiscard = (draft: UserDraft) => {
    draftManager.deleteDraft(draft.id);
    setDeleteItem(null);
    setNotification(`Draft "${draft.title}" was discarded.`);
    refreshDrafts();
  };

  const handleClearAll = () => {
    const filterType = selectedType !== 'all' ? (selectedType as DraftEntityType) : undefined;
    draftManager.clearAllDrafts(filterType);
    setClearAllOpen(false);
    setNotification(filterType ? `All ${filterType} drafts cleared.` : 'All drafts cleared.');
    refreshDrafts();
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-gray-50 overflow-hidden">
      {/* ── Top Header ────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="text-2xl">📝</span>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">Drafts</h1>
              <span className="text-xs font-semibold bg-brand-100 text-brand-700 px-2.5 py-0.5 rounded-full">
                {drafts.length} {drafts.length === 1 ? 'draft' : 'drafts'}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Work-in-progress forms saved automatically. If you close a modal accidentally, recover and continue here.
            </p>
          </div>

          {drafts.length > 0 && (
            <button
              onClick={() => setClearAllOpen(true)}
              className="self-start sm:self-auto text-xs font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-md border border-red-200 transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
              <span>Clear All Drafts</span>
            </button>
          )}
        </div>

        {/* ── Filter Bar & Search ──────────────────────────────────── */}
        <div className="mt-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          {/* Entity Togglable Pills */}
          <div className="flex items-center flex-wrap gap-1.5 no-scrollbar">
            <button
              onClick={() => setSelectedType('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                selectedType === 'all'
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({summary.all})
            </button>

            {(Object.keys(TYPE_CONFIG) as DraftEntityType[]).map((type) => {
              const cfg = TYPE_CONFIG[type];
              const count = summary[type] || 0;
              const isActive = selectedType === type;
              return (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                    isActive
                      ? 'bg-brand-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{cfg.icon}</span>
                  <span>{cfg.label}</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                      isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search Input */}
          <div className="relative w-full md:w-64 flex-shrink-0">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search drafts..."
              className="input text-xs h-8 pl-8 pr-7 w-full"
            />
            <svg
              className="w-4 h-4 text-gray-400 absolute left-2.5 top-2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600 text-xs font-bold"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Notification Toast ────────────────────────────────────── */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="mx-4 sm:mx-6 mt-3 px-3.5 py-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium rounded-lg shadow-sm flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <span>✓</span>
              <span>{notification}</span>
            </div>
            <button onClick={() => setNotification('')} className="text-emerald-600 hover:text-emerald-800 font-bold">
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Main List Container ───────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {filteredDrafts.length === 0 ? (
          <div className="h-full min-h-[320px] flex flex-col items-center justify-center text-center p-8 bg-white rounded-xl border border-dashed border-gray-300">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-2xl mb-3 shadow-inner">
              📝
            </div>
            <h3 className="text-base font-semibold text-gray-900">No Unsaved Drafts</h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 max-w-md">
              {search
                ? `No drafts match the filter "${search}". Try clearing your search.`
                : 'When you start filling out any Training certificate, Form 10, Form 11, Equipment, or Company form, your progress is automatically saved here.'}
            </p>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="mt-3 text-xs text-brand-600 hover:text-brand-700 font-semibold"
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredDrafts.map((draft) => {
              const cfg = TYPE_CONFIG[draft.entity_type] || {
                label: draft.entity_type,
                icon: '📄',
                path: '/',
                bg: 'bg-gray-50',
                border: 'border-gray-200',
                text: 'text-gray-700',
              };
              const isExpanded = expandedIds.has(draft.id);
              const dataEntries = Object.entries(draft.form_data || {}).filter(
                ([k, v]) => v !== '' && v !== null && v !== undefined && k !== 'fields'
              );

              return (
                <div
                  key={draft.id}
                  className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-all shadow-sm overflow-hidden"
                >
                  {/* Card Header / Summary Row */}
                  <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg flex-shrink-0 ${cfg.bg} ${cfg.border} border`}
                      >
                        {cfg.icon}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${cfg.bg} ${cfg.text} border ${cfg.border}`}
                          >
                            {cfg.label}
                          </span>
                          <span className="text-xs text-gray-400 font-medium">
                            • Updated {formatRelativeTime(draft.updated_at)}
                          </span>
                        </div>

                        <h3 className="text-sm sm:text-base font-semibold text-gray-900 mt-1 truncate">
                          {draft.title || 'Untitled Draft'}
                        </h3>

                        {draft.subtitle && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{draft.subtitle}</p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
                      {/* Accordion Toggle Details */}
                      <button
                        onClick={() => toggleDetails(draft.id)}
                        className="btn btn-secondary text-xs px-2.5 py-1.5 h-8 flex items-center gap-1 cursor-pointer font-medium"
                      >
                        <span>{isExpanded ? 'Hide Details' : 'Details'}</span>
                        <span className="text-[10px]">{isExpanded ? '▲' : '▼'}</span>
                      </button>

                      {/* Discard Button */}
                      <button
                        onClick={() => setDeleteItem(draft)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors cursor-pointer"
                        title="Discard Draft"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                        </svg>
                      </button>

                      {/* Resume / Continue Button */}
                      <button
                        onClick={() => handleResume(draft)}
                        className="btn btn-primary text-xs px-3.5 py-1.5 h-8 flex items-center gap-1.5 cursor-pointer font-semibold shadow-sm"
                      >
                        <span>Resume</span>
                        <span>→</span>
                      </button>
                    </div>
                  </div>

                  {/* ── Togglable Details Accordion ───────────────────── */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.18 }}
                        className="border-t border-gray-100 bg-slate-50/75 px-4 py-3"
                      >
                        <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                          Saved Form Snapshot
                        </div>
                        {dataEntries.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">No field details saved yet.</p>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                            {dataEntries.map(([key, val]) => (
                              <div
                                key={key}
                                className="bg-white rounded-md p-2 border border-gray-200 text-xs overflow-hidden"
                              >
                                <div className="text-[10px] font-medium text-gray-400 uppercase truncate">
                                  {key.replace(/_/g, ' ')}
                                </div>
                                <div className="font-medium text-gray-800 truncate mt-0.5">
                                  {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Confirm Single Discard ──────────────────────────────────── */}
      <ConfirmDialog
        open={!!deleteItem}
        title="Discard Draft?"
        message={`Are you sure you want to discard the draft for "${deleteItem?.title || 'this form'}"? This action cannot be undone.`}
        confirmLabel="Discard Draft"
        variant="danger"
        onConfirm={() => { if (deleteItem) handleDiscard(deleteItem); }}
        onCancel={() => setDeleteItem(null)}
      />

      {/* ── Confirm Clear All ───────────────────────────────────────── */}
      <ConfirmDialog
        open={clearAllOpen}
        title="Clear All Drafts?"
        message={`Are you sure you want to delete all drafts${
          selectedType !== 'all' ? ` for ${selectedType}` : ''
        }? Any unsaved work will be permanently lost.`}
        confirmLabel="Clear All"
        variant="danger"
        onConfirm={handleClearAll}
        onCancel={() => setClearAllOpen(false)}
      />
    </div>
  );
}
